import {
  FiledType,
  FinanceType,
  LanguageType,
  PP30FilingRoundType,
  PP30FormStatus,
  PP30TaxCreditType,
  PP30TaxRefundType,
  SettingVatFormType,
  TransactionType,
} from "@/enums/enums";
import { ElementDefaultModel } from "@/model/document/documentModel";
import {
  DetailPP30TransactionDocumentModel,
  ElementPP30TransactionCertifiedModel,
  ElementPP30TransactionInfoModel,
  ElementPP30TransactionItemModel,
  FooterPP30TransactionDocumentModel,
  HeaderPP30TransactionDocumentModel,
  HeaderPurchasePP30TransactionColumnModel,
  HeaderSalePP30TransactionColumnModel,
  PP30DocumentModel,
  PP30DocumentTemplateModel,
  PP30OnlineViewModel,
  PP30TransactionModel,
} from "@/model/document/pp30DocumentModel";
import { SettingPP30DocumentTemplateModel } from "@/model/document/settingDocumentTemplate";
import { MerchantDetail } from "@/model/merchant/merchantModel";
import { UserInfoModel } from "@/model/user/userModel";
import { GetPP30SettingDocument } from "@/service/document/pp30/pp30Facade";
import {
  GetDocumentMerchant,
  GetDocumentTags,
  GetDocumentUser,
  GetPP30TransactionModelList,
} from "./documentFacadeHelper";
import {
  GetHeadType,
  GetMerchant,
  GetMerchantLogo,
  GetSignatureUrlUserApprove,
  GetSignatureUrlUserCreate,
  SetLanguage,
} from "./documentHelper";

export function GetPP30DocumentData(data: any): PP30DocumentModel {
  const documentDataModel: PP30DocumentModel = {
    approveDateTime: data.approveDateTime ? data.approveDateTime.ToDateTimezone() : "",
    approveUserId: data.approveUserId ? data.approveUserId : 0,
    changeTaxCreditAmount: data.changeTaxCreditAmount ? data.changeTaxCreditAmount : 0,
    countPurchaseTransaction: data.countPurchaseTransaction ? data.countPurchaseTransaction : 0,
    countSaleTransaction: data.countSaleTransaction ? data.countSaleTransaction : 0,
    createDateTime: data.createDateTime ? data.createDateTime.ToDateTimezone() : "",
    createUserId: data.createUserId ? data.createUserId : 0,
    filingRound: data.filingRound ? data.filingRound : 0,
    filingRoundType: data.filingRoundType ? data.filingRoundType : PP30FilingRoundType.None,
    filingType: data.filingType ? data.filingType : FiledType.None,
    fineAmount: data.fineAmount ? data.fineAmount : 0,
    formDate: data.formDate ? data.formDate : "",
    formNumber: data.formNumber ? data.formNumber : "",
    interestAmount: data.interestAmount ? data.interestAmount : 0,
    isClosingEntry: data.isClosingEntry ? data.isClosingEntry : 0,
    isDeleted: data.isDeleted ? data.isDeleted : false,
    isFiled: data.isFiled ? data.isFiled : false,
    isPaid: data.isPaid ? data.isPaid : false,
    netTaxAmount: data.netTaxAmount ? data.netTaxAmount : 0,
    pp30Id: data.pp30Id ? data.pp30Id : 0,
    purchaseTaxAmount: data.purchaseTaxAmount ? data.purchaseTaxAmount : 0,
    purchaseTotal: data.purchaseTotal ? data.purchaseTotal : 0,
    remainingTaxCreditAmount: data.remainingTaxCreditAmount ? data.remainingTaxCreditAmount : 0,
    saleTotal: data.saleTotal ? data.saleTotal : 0,
    salesTaxAmount: data.salesTaxAmount ? data.salesTaxAmount : 0,
    status: data.status ? data.status : PP30FormStatus.AwaitFiling,
    taxCreditType: data.taxCreditType ? data.taxCreditType : PP30TaxCreditType.None,
    taxRefundType: data.taxRefundType ? data.taxRefundType : PP30TaxRefundType.None,
    updateDateTime: data.updateDateTime ? data.updateDateTime.ToDateTimezone() : "",
    updateUserId: data.updateUserId ? data.updateUserId : 0,
    vatApplicableSaleAmount: data.vatApplicableSaleAmount ? data.vatApplicableSaleAmount : 0,
    vatClaimablePurchaseAmount: data.vatClaimablePurchaseAmount ? data.vatClaimablePurchaseAmount : 0,
    vatExcemptedPurchaseAmount: data.vatExcemptedPurchaseAmount ? data.vatExcemptedPurchaseAmount : 0,
    vatExcemptedSaleAmount: data.vatExcemptedSaleAmount ? data.vatExcemptedSaleAmount : 0,
    vatToPayTotal: data.vatToPayTotal ? data.vatToPayTotal : 0,
    vatToRefundTotal: data.vatToRefundTotal ? data.vatToRefundTotal : 0,
    vatZeroPurchaseAmount: data.vatZeroPurchaseAmount ? data.vatZeroPurchaseAmount : 0,
    vatZeroSaleAmount: data.vatZeroSaleAmount ? data.vatZeroSaleAmount : 0,
    createdDateTime: data.createdDateTime ? data.createdDateTime.ToDateTimezone() : "",
    createdUser: data.createdUser ? GetDocumentUser(data.createdUser) : new UserInfoModel(),
    updatedDateTime: data.updatedDateTime ? data.updatedDateTime.ToDateTimezone() : "",
    updatedUser: data.updatedUser ? GetDocumentUser(data.updatedUser) : new UserInfoModel(),
    approvedDateTime: data.approvedDateTime ? data.approvedDateTime.ToDateTimezone() : "",
    approvedUser: data.approvedUser ? GetDocumentUser(data.approvedUser) : new UserInfoModel(),
    merchant: data.merchant ? GetDocumentMerchant(data.merchant) : new MerchantDetail(),
    tags: data.tags ? GetDocumentTags(data.tags) : [],
    pp30TransactionPurchaseTaxModelList: data.pp30TransactionPurchaseTaxModelList
      ? GetPP30TransactionModelList(data.pp30TransactionPurchaseTaxModelList)
      : new Array<PP30TransactionModel>(),
    pp30TransactionSalesTaxModelList: data.pp30TransactionSalesTaxModelList
      ? GetPP30TransactionModelList(data.pp30TransactionSalesTaxModelList)
      : new Array<PP30TransactionModel>(),
  };

  return documentDataModel;
}

export function GetPP30DocumentTemplate(
  documentModel: PP30OnlineViewModel,
  settingDocumentTemplateDefault?: SettingPP30DocumentTemplateModel
): PP30DocumentTemplateModel {
  const documentTemplate = new PP30DocumentTemplateModel();

  documentTemplate.transactionType = TransactionType.PP30;
  documentTemplate.merchant = documentModel.document.data.merchant;
  documentTemplate.pp30FormNumber = documentModel.document.data.formNumber;
  documentTemplate.taxPeriodDate = new Date(documentModel.document.data.formDate);
  documentTemplate.filingType = documentModel.document.data.filingType;
  documentTemplate.times = documentModel.document.data.filingRound;
  documentTemplate.filingRoundType = documentModel.document.data.filingRoundType;
  documentTemplate.pp30TaxCreditType = documentModel.document.data.taxCreditType;
  documentTemplate.pp30TaxRefundType = documentModel.document.data.taxRefundType;
  documentTemplate.vatZeroSaleAmount = documentModel.document.data.vatZeroSaleAmount;
  documentTemplate.vatExcemptedSaleAmount = documentModel.document.data.vatExcemptedSaleAmount;
  documentTemplate.vatApplicableSaleAmount = documentModel.document.data.vatApplicableSaleAmount;
  documentTemplate.salesTaxAmount = documentModel.document.data.salesTaxAmount;
  documentTemplate.vatZeroPurchaseAmount = documentModel.document.data.vatZeroPurchaseAmount;
  documentTemplate.vatExcemptedPurchaseAmount = documentModel.document.data.vatExcemptedPurchaseAmount;
  documentTemplate.vatClaimablePurchaseAmount = documentModel.document.data.vatClaimablePurchaseAmount;
  documentTemplate.purchaseTaxAmount = documentModel.document.data.purchaseTaxAmount;
  documentTemplate.saleTotal = documentModel.document.data.saleTotal;
  documentTemplate.purchaseTotal = documentModel.document.data.purchaseTotal;
  documentTemplate.countSaleTransaction = documentModel.document.data.countSaleTransaction;
  documentTemplate.countPurchaseTransaction = documentModel.document.data.countPurchaseTransaction;
  documentTemplate.netTaxAmount = documentModel.document.data.netTaxAmount;
  documentTemplate.vatToPayTotalThisMonth = 0;
  documentTemplate.vatToRefundTotalThisMonth = 0;
  documentTemplate.changeTaxCreditAmount = documentModel.document.data.changeTaxCreditAmount;
  documentTemplate.vatToPayTotal = documentModel.document.data.vatToPayTotal;
  documentTemplate.vatToRefundTotal = documentModel.document.data.vatToRefundTotal;
  documentTemplate.remainingTaxCreditAmount = documentModel.document.data.remainingTaxCreditAmount;
  documentTemplate.interestAmount = documentModel.document.data.interestAmount;
  documentTemplate.fineAmount = documentModel.document.data.fineAmount;
  documentTemplate.taxTotal = 0;
  documentTemplate.taxTotalOverpaid = 0;
  documentTemplate.userApprove = {
    isShowSignatureUrl: documentModel.template.certified.isShowUserApproveUrl ?? true,
    isShowApproveDate: documentModel.template.certified.isShowUserApproveDate ?? true,
    isShowApproveName: documentModel.template.certified.isShowUserApproveName ?? true,
    approveDate: new Date(documentModel.document.data.approvedDateTime),
    name: documentModel.document.data.approvedUser.firstName + " " + documentModel.document.data.approvedUser.lastName,
    signatureUrl: GetSignatureUrlUserApprove(documentModel),
  };
  documentTemplate.userCreate = {
    isShowSignatureUrl: documentModel.template.certified.isShowUserCreateUrl ?? true,
    isShowCreateDate: documentModel.template.certified.isShowUserCreateDate ?? true,
    isShowCreateName: documentModel.template.certified.isShowUserCreateName ?? true,
    createDate: new Date(documentModel.document.data.createdDateTime),
    name: documentModel.document.data.createdUser.firstName + " " + documentModel.document.data.createdUser.lastName,
    signatureUrl: GetSignatureUrlUserCreate(documentModel),
  };
  documentTemplate.merchantStampUrl = {
    value: documentModel.credential.merchantCredential.stampUrl,
    isShowElement: documentModel.template.certified.isShowMerchantStampUrl,
  };
  documentTemplate.pp30TransactionPurchaseTaxModelList =
    documentModel.document.data.pp30TransactionPurchaseTaxModelList;
  documentTemplate.pp30TransactionSalesTaxModelList = documentModel.document.data.pp30TransactionSalesTaxModelList;
  documentTemplate.isDeleted = documentModel.document.data.isDeleted;
  documentTemplate.financeType = documentModel.financeType;
  documentTemplate.expenseType = documentModel.expenseType;
  documentTemplate.header = GetPP30TransactionHeaderDocument(documentModel, settingDocumentTemplateDefault);
  documentTemplate.detail = GetPP30TransactionBodyDocument(documentModel, settingDocumentTemplateDefault);
  documentTemplate.footer = GetPP30TransactionFooterDocment(documentModel, settingDocumentTemplateDefault);

  if (settingDocumentTemplateDefault) {
    documentTemplate.userApprove.isShowSignatureUrl =
      settingDocumentTemplateDefault.certified.isShowUserApproveUrl ?? true;
    documentTemplate.userApprove.isShowApproveDate =
      settingDocumentTemplateDefault.certified.isShowUserApproveDate ?? true;
    documentTemplate.userCreate.isShowSignatureUrl =
      settingDocumentTemplateDefault.certified.isShowUserCreateUrl ?? true;
    documentTemplate.userCreate.isShowCreateDate =
      settingDocumentTemplateDefault.certified.isShowUserCreateDate ?? true;
    documentTemplate.merchantStampUrl.isShowElement = settingDocumentTemplateDefault.certified.isShowMerchantStampUrl;
  }

  return documentTemplate;
}

export function GetPP30TransactionHeaderDocument(
  documentModel: PP30OnlineViewModel,
  settingDocumentTemplateDefault?: SettingPP30DocumentTemplateModel
): HeaderPP30TransactionDocumentModel {
  let defaultTemplate: SettingPP30DocumentTemplateModel = GetPP30SettingDocument(documentModel.financeType);

  const headerDocument: HeaderPP30TransactionDocumentModel = {
    colorCode: documentModel.template.colorCode,
    isShowColorBackground: documentModel.template.isShowColorBackground ?? defaultTemplate.isShowColorBackground,
    isShowColorBorder: documentModel.template.isShowColorBorder ?? defaultTemplate.isShowColorBorder,
    language: documentModel.template.language ?? LanguageType.Th,
    merchantLogo: GetMerchantLogo(documentModel, settingDocumentTemplateDefault),
    headType: GetHeadType(documentModel, settingDocumentTemplateDefault),
    merchant: GetMerchant(documentModel, settingDocumentTemplateDefault),
    documentInfo: GetPP30TransactionInfo(documentModel, settingDocumentTemplateDefault),
    documentName: GetPP30TransactionName(documentModel, settingDocumentTemplateDefault),
  };

  if (settingDocumentTemplateDefault) {
    headerDocument.colorCode = settingDocumentTemplateDefault.colorCode;
    headerDocument.isShowColorBackground = settingDocumentTemplateDefault.isShowColorBackground;
    headerDocument.isShowColorBorder = settingDocumentTemplateDefault.isShowColorBorder;
    headerDocument.language = settingDocumentTemplateDefault.language ?? LanguageType.Th;
  }
  SetLanguage(headerDocument.language);
  return headerDocument;
}

export function GetPP30TransactionName(
  documentModel: PP30OnlineViewModel,
  settingDocumentTemplateDefault?: SettingPP30DocumentTemplateModel
): ElementDefaultModel {
  let documentName: ElementDefaultModel = {
    isShowElement: documentModel.template.documentName.isShowElement,
    value: documentModel.template.documentName.value,
  };

  if (settingDocumentTemplateDefault) {
    documentName.isShowElement = settingDocumentTemplateDefault.documentName.isShowElement;
    documentName.value = settingDocumentTemplateDefault.documentName.value;
  }
  return documentName;
}

export function GetPP30TransactionInfo(
  documentModel: PP30OnlineViewModel,
  settingDocumentTemplateDefault?: SettingPP30DocumentTemplateModel
): ElementPP30TransactionInfoModel {
  const pp30TransactionInfo: ElementPP30TransactionInfoModel = {
    formNumberTitle: {
      value: documentModel.template.documentInfo.formNumberTitle.value,
      isShowElement: documentModel.template.documentInfo.formNumberTitle.isShowElement,
    },
    formDateTitle: {
      value: documentModel.template.documentInfo.formDateTitle.value,
      isShowElement: documentModel.template.documentInfo.formDateTitle.isShowElement,
    },
    issueDateTitle: {
      value: documentModel.template.documentInfo.issueDateTitle.value,
      isShowElement: documentModel.template.documentInfo.issueDateTitle.isShowElement,
    },
    formNumber: documentModel.document.data.formNumber,
    formDate: new Date(documentModel.document.data.formDate),
    issueDate: new Date(documentModel.document.data.createdDateTime),
    isCustomTextDisplay: documentModel.template.documentInfo.isCustomTextDisplay,
    dateOptionType: documentModel.template.documentInfo.dateOptionType,
  };

  if (settingDocumentTemplateDefault) {
    pp30TransactionInfo.formNumberTitle.isShowElement =
      settingDocumentTemplateDefault.documentInfo.formNumberTitle.isShowElement;
    pp30TransactionInfo.formDateTitle.isShowElement =
      settingDocumentTemplateDefault.documentInfo.formDateTitle.isShowElement;
    pp30TransactionInfo.issueDateTitle.isShowElement =
      settingDocumentTemplateDefault.documentInfo.issueDateTitle.isShowElement;
    pp30TransactionInfo.isCustomTextDisplay = settingDocumentTemplateDefault.documentInfo.isCustomTextDisplay;
    pp30TransactionInfo.dateOptionType = settingDocumentTemplateDefault.documentInfo.dateOptionType;
  }
  return pp30TransactionInfo;
}

export function GetPP30TransactionBodyDocument(
  documentModel: PP30OnlineViewModel,
  settingDocumentTemplateDefault?: SettingPP30DocumentTemplateModel
): DetailPP30TransactionDocumentModel {
  const detail: DetailPP30TransactionDocumentModel = {
    itemList: GetPP30TransactionItemList(documentModel),
    settingVatFormType: documentModel.template.settingVatFormType,
  };

  if (settingDocumentTemplateDefault) {
    detail.settingVatFormType = settingDocumentTemplateDefault.settingVatFormType;
  }
  return detail;
}

export function GetPP30TransactionItemList(documentModel: PP30OnlineViewModel): Array<ElementPP30TransactionItemModel> {
  const pp30TransactionItemList = new Array<ElementPP30TransactionItemModel>();
  const itemstList =
    documentModel.financeType === FinanceType.InFlow
      ? documentModel.document.data.pp30TransactionSalesTaxModelList
      : documentModel.document.data.pp30TransactionPurchaseTaxModelList;

  if (itemstList && itemstList.length > 0) {
    itemstList.forEach((element) => {
      const TaxItem: ElementPP30TransactionItemModel = {
        sequenceNumber: element.sequenceNumber,
        taxInvoiceDate: element.taxInvoiceDate,
        taxInvoiceNumber: element.taxInvoiceNumber,
        contact: element.documentContactModel,
        vatExemptedTotal: element.vatExemptedTotal,
        zeroVatTotal: element.zeroVatTotal,
        preVatTotal: element.preVatTotal,
        vatTotal: element.vatTotal,
      };

      pp30TransactionItemList.push(TaxItem);
    });
  }

  return pp30TransactionItemList;
}

export function GetPP30TransactionFooterDocment(
  documentModel: PP30OnlineViewModel,
  settingDocumentTemplateDefault?: SettingPP30DocumentTemplateModel
): FooterPP30TransactionDocumentModel {
  const footer: FooterPP30TransactionDocumentModel = {
    itemPurchase: documentModel.document.data.countPurchaseTransaction,
    preVatPurchaseAmount:
      documentModel.document.data.vatExcemptedPurchaseAmount +
      documentModel.document.data.vatZeroPurchaseAmount +
      documentModel.document.data.vatClaimablePurchaseAmount,
    vatExemptedPurchaseTotal: documentModel.document.data.vatExcemptedPurchaseAmount,
    zeroVatPurchaseTotal: documentModel.document.data.vatZeroPurchaseAmount,
    preVatPurchaseTotal: documentModel.document.data.vatClaimablePurchaseAmount,
    vatPurchaseTotal: documentModel.document.data.purchaseTaxAmount,
    itemSale: documentModel.document.data.countSaleTransaction,
    preVatSaleAmount:
      documentModel.document.data.vatExcemptedSaleAmount +
      documentModel.document.data.vatZeroSaleAmount +
      documentModel.document.data.vatApplicableSaleAmount,
    vatExemptedSaleTotal: documentModel.document.data.vatExcemptedSaleAmount,
    zeroVatSaleTotal: documentModel.document.data.vatZeroSaleAmount,
    preVatSaleTotal: documentModel.document.data.vatApplicableSaleAmount,
    vatSaleTotal: documentModel.document.data.salesTaxAmount,
    certified: GetPP30TransactionCertified(documentModel, settingDocumentTemplateDefault),
  };
  return footer;
}

export function GetPP30TransactionCertified(
  documentModel: PP30OnlineViewModel,
  settingDocumentTemplateDefault?: SettingPP30DocumentTemplateModel
): ElementPP30TransactionCertifiedModel {
  const certified: ElementPP30TransactionCertifiedModel = {
    userCreateTitle: {
      value: documentModel.template.pp30TransactionCertified.userCreateTitle.value,
      isShowElement: documentModel.template.pp30TransactionCertified.userCreateTitle.isShowElement,
    },
    userApproveTitle: {
      value: documentModel.template.pp30TransactionCertified.userApproveTitle.value,
      isShowElement: documentModel.template.pp30TransactionCertified.userApproveTitle.isShowElement,
    },
    userApprove: {
      isShowSignatureUrl: documentModel.template.pp30TransactionCertified.isShowUserApproveUrl ?? true,
      isShowApproveDate: documentModel.template.pp30TransactionCertified.isShowUserApproveDate ?? true,
      isShowApproveName: documentModel.template.pp30TransactionCertified.isShowUserApproveName ?? true,
      approveDate: new Date(documentModel.document.data.approvedDateTime),
      name:
        documentModel.document.data.approvedUser.firstName + " " + documentModel.document.data.approvedUser.lastName,
      signatureUrl: GetSignatureUrlUserApprove(documentModel),
    },
    userCreate: {
      isShowSignatureUrl: documentModel.template.pp30TransactionCertified.isShowUserCreateUrl ?? true,
      isShowCreateDate: documentModel.template.pp30TransactionCertified.isShowUserCreateDate ?? true,
      isShowCreateName: documentModel.template.pp30TransactionCertified.isShowUserCreateName ?? true,
      createDate: new Date(documentModel.document.data.createdDateTime),
      name: documentModel.document.data.createdUser.firstName + " " + documentModel.document.data.createdUser.lastName,
      signatureUrl: GetSignatureUrlUserCreate(documentModel),
    },
    isCustomTextDisplay: documentModel.template.pp30TransactionCertified.isCustomTextDisplay,
    isShowQRCode: documentModel.template.pp30TransactionCertified.isShowQRCode ?? true,
  };

  if (settingDocumentTemplateDefault) {
    certified.userCreateTitle.value = settingDocumentTemplateDefault.pp30TransactionCertified.userCreateTitle.value;
    certified.userCreateTitle.isShowElement =
      settingDocumentTemplateDefault.pp30TransactionCertified.userCreateTitle.isShowElement;
    certified.userApproveTitle.value = settingDocumentTemplateDefault.pp30TransactionCertified.userApproveTitle.value;
    certified.userApproveTitle.isShowElement =
      settingDocumentTemplateDefault.pp30TransactionCertified.userApproveTitle.isShowElement;
    certified.userApprove.isShowSignatureUrl =
      settingDocumentTemplateDefault.pp30TransactionCertified.isShowUserApproveUrl ?? true;
    certified.userApprove.isShowApproveDate =
      settingDocumentTemplateDefault.pp30TransactionCertified.isShowUserApproveDate ?? true;
    certified.userCreate.isShowSignatureUrl =
      settingDocumentTemplateDefault.pp30TransactionCertified.isShowUserCreateUrl ?? true;
    certified.userCreate.isShowCreateDate =
      settingDocumentTemplateDefault.pp30TransactionCertified.isShowUserCreateDate ?? true;
    certified.isCustomTextDisplay = settingDocumentTemplateDefault.pp30TransactionCertified.isCustomTextDisplay;
    certified.isShowQRCode = settingDocumentTemplateDefault.pp30TransactionCertified.isShowQRCode ?? true;
  }

  return certified;
}

export function GetHeaderSalePP30TransactionColumn(
  documentTemplate: PP30DocumentTemplateModel
): HeaderSalePP30TransactionColumnModel {
  let contactWidth = 715; //maxWidth 753px
  let headerItemColumn: HeaderSalePP30TransactionColumnModel = {
    index: {
      isShowColumn: true,
      width: 35,
      align: "left",
    },
    taxInvoiceDate: {
      isShowColumn: true,
      width: 80,
      align: "center",
    },
    taxInvoiceNumber: {
      isShowColumn: true,
      width: 100,
      align: "left",
    },
    contact: {
      isShowColumn: true,
      width: 0,
      align: "left",
    },
    taxId: {
      isShowColumn: true,
      width: 120,
      align: "center",
    },
    branch: {
      isShowColumn: true,
      width: 45,
      align: "center",
    },
    preVatAmount: {
      isShowColumn: documentTemplate.detail.settingVatFormType === SettingVatFormType.IncludedPreVat,
      width: 110,
      align: "right",
    },
    vatExemptedTotal: {
      isShowColumn: documentTemplate.detail.settingVatFormType === SettingVatFormType.SeparatePreVat,
      width: 70,
      align: "right",
    },
    zeroVatTotal: {
      isShowColumn: documentTemplate.detail.settingVatFormType === SettingVatFormType.SeparatePreVat,
      width: 70,
      align: "right",
    },
    preVatTotal: {
      isShowColumn: documentTemplate.detail.settingVatFormType === SettingVatFormType.SeparatePreVat,
      width: 70,
      align: "right",
    },
    vatTotal: {
      isShowColumn: true,
      width: 80,
      align: "right",
    },
  };

  for (const [key, value] of Object.entries(headerItemColumn)) {
    if (value.isShowColumn) {
      contactWidth -= value.width;
    }
  }

  headerItemColumn.contact.width = contactWidth;

  return headerItemColumn;
}

export function GetHeaderPurchasePP30TransactionColumn(): HeaderPurchasePP30TransactionColumnModel {
  let contactWidth = 715; //maxWidth 753px
  let headerItemColumn: HeaderPurchasePP30TransactionColumnModel = {
    index: {
      isShowColumn: true,
      width: 35,
      align: "left",
    },
    taxInvoiceDate: {
      isShowColumn: true,
      width: 80,
      align: "center",
    },
    taxInvoiceNumber: {
      isShowColumn: true,
      width: 100,
      align: "left",
    },
    contact: {
      isShowColumn: true,
      width: 0,
      align: "left",
    },
    taxId: {
      isShowColumn: true,
      width: 120,
      align: "center",
    },
    branch: {
      isShowColumn: true,
      width: 45,
      align: "center",
    },
    preVatTotal: {
      isShowColumn: true,
      width: 110,
      align: "right",
    },
    vatTotal: {
      isShowColumn: true,
      width: 80,
      align: "right",
    },
  };

  for (const [key, value] of Object.entries(headerItemColumn)) {
    if (value.isShowColumn) {
      contactWidth -= value.width;
    }
  }

  headerItemColumn.contact.width = contactWidth;

  return headerItemColumn;
}
